import * as React from 'react'

import type { ListAction, ListNode } from '~/client/src/shared/lists/core'
import {
  clearSelection,
  setIdsSelected,
  toggleId,
} from '~/client/src/shared/lists/core'
import { recordIdsBetween } from '~/client/src/shared/lists/recordIdsBetween'
import { isEventFromOwnDom } from '~/client/src/shared/utils/isEventFromOwnDom'

export interface ListActivationEvent extends React.SyntheticEvent {
  readonly shiftKey?: boolean
  readonly metaKey?: boolean
  readonly ctrlKey?: boolean
}

interface ListInteractionOptions {
  readonly onActivate?: (recordId: string, event: ListActivationEvent) => void
  readonly holdToSelect?: boolean
  readonly activateSelects?: boolean
  readonly holdDelayMs?: number
  readonly holdMoveTolerancePx?: number
  readonly scopeKey?: string
}

interface ListInteractionHandlers {
  onClick(e: React.MouseEvent): void
  onPointerDown(e: React.PointerEvent): void
  onPointerMove(e: React.PointerEvent): void
  onPointerUp(e: React.PointerEvent): void
  onPointerCancel(e: React.PointerEvent): void
}

interface PressState {
  readonly x: number
  readonly y: number
  readonly timeoutId: number
}

export interface ListInteractionSource<T> {
  readonly nodes: readonly ListNode<T>[]
  readonly selection: { readonly count: number }
  dispatch(action: ListAction): void
}

export function useListInteraction<T>(
  list: ListInteractionSource<T>,
  opts: ListInteractionOptions = {},
): {
  isSelectionMode: boolean
  handlersFor(recordId: string): ListInteractionHandlers
  onSelectionMouseDown(event: React.MouseEvent): void
  activateFor(recordId: string, event: ListActivationEvent): boolean
  toggleFor(recordId: string, event?: { shiftKey?: boolean }): void
  exitSelectionMode(): void
} {
  const [isSelectionMode, setSelectionMode] = React.useState(false)
  const anchorRef = React.useRef<string | null>(null)
  const pressRef = React.useRef<PressState | null>(null)
  const swallowNextClickRef = React.useRef(false)
  const { onActivate, holdToSelect } = opts
  const activateSelects = opts.activateSelects ?? false
  const holdDelayMs = opts.holdDelayMs ?? 500
  const holdMoveTolerancePx = opts.holdMoveTolerancePx ?? 10

  const clearHoldTimer = React.useCallback(() => {
    const press = pressRef.current
    if (press) {
      window.clearTimeout(press.timeoutId)
      pressRef.current = null
    }
  }, [])

  React.useEffect(() => {
    return () => {
      clearHoldTimer()
    }
  }, [clearHoldTimer])

  React.useEffect(() => {
    anchorRef.current = null
    swallowNextClickRef.current = false
    clearHoldTimer()
    setSelectionMode(false)
  }, [clearHoldTimer, opts.scopeKey])

  React.useEffect(() => {
    if (isSelectionMode && list.selection.count === 0) {
      setSelectionMode(false)
    }
  }, [isSelectionMode, list.selection.count])

  const exitSelectionMode = React.useCallback(() => {
    setSelectionMode(false)
    list.dispatch(clearSelection())
  }, [list])

  const onSelectionMouseDown = React.useCallback(
    (event: React.MouseEvent): void => {
      if (event.shiftKey) event.preventDefault()
    },
    [],
  )

  const toggleFor = React.useCallback(
    (recordId: string, event?: { shiftKey?: boolean }): void => {
      if (event?.shiftKey && anchorRef.current) {
        const ids = recordIdsBetween(list.nodes, anchorRef.current, recordId)
        list.dispatch(setIdsSelected(ids, true))
        return
      }

      list.dispatch(toggleId(recordId))
      anchorRef.current = recordId
    },
    [list],
  )

  const activateFor = React.useCallback(
    (recordId: string, e: ListActivationEvent): boolean => {
      if (swallowNextClickRef.current) {
        swallowNextClickRef.current = false
        return false
      }

      if (isSelectionMode) {
        list.dispatch(toggleId(recordId))
        if (list.selection.count === 0) {
          setSelectionMode(false)
        }
        return false
      }

      if (e.shiftKey) {
        const ids = recordIdsBetween(list.nodes, anchorRef.current, recordId)
        list.dispatch(setIdsSelected(ids, true))
        if (!anchorRef.current) {
          anchorRef.current = recordId
        }
        return false
      }

      if (e.metaKey || e.ctrlKey) {
        toggleFor(recordId)
        return false
      }

      if (activateSelects) {
        anchorRef.current = recordId
        list.dispatch(clearSelection())
        list.dispatch(setIdsSelected([recordId], true))
      }
      onActivate?.(recordId, e)
      return true
    },
    [activateSelects, isSelectionMode, list, onActivate, toggleFor],
  )

  const handlersFor = React.useCallback(
    (recordId: string): ListInteractionHandlers => ({
      onClick(e) {
        if (!isEventFromOwnDom(e)) return

        activateFor(recordId, e)
      },
      onPointerDown(e) {
        if (!holdToSelect) return
        if (!isEventFromOwnDom(e)) return

        swallowNextClickRef.current = false
        clearHoldTimer()
        const timeoutId = window.setTimeout(() => {
          pressRef.current = null
          swallowNextClickRef.current = true
          setSelectionMode(true)
          list.dispatch(setIdsSelected([recordId], true))
          anchorRef.current = recordId
        }, holdDelayMs)

        pressRef.current = {
          x: e.clientX,
          y: e.clientY,
          timeoutId,
        }
      },
      onPointerMove(e) {
        if (!holdToSelect) return

        const press = pressRef.current
        if (!press) return

        const dx = Math.abs(e.clientX - press.x)
        const dy = Math.abs(e.clientY - press.y)
        if (dx > holdMoveTolerancePx || dy > holdMoveTolerancePx) {
          clearHoldTimer()
        }
      },
      onPointerUp() {
        if (!holdToSelect) return

        clearHoldTimer()
      },
      onPointerCancel() {
        if (!holdToSelect) return

        clearHoldTimer()
      },
    }),
    [
      clearHoldTimer,
      holdDelayMs,
      holdMoveTolerancePx,
      holdToSelect,
      list,
      activateFor,
    ],
  )

  return {
    isSelectionMode,
    handlersFor,
    onSelectionMouseDown,
    activateFor,
    toggleFor,
    exitSelectionMode,
  }
}
